import { useCallback, useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";

import FormFooterEdit from "components/Forms/FooterEdit";
import FormSourceCode from "components/Forms/SourceCode";
import { SourceCodeProjects } from "components/Forms/SourceCode/types";
import useTypedContext from "hooks/useTypedContext";
import { VcsProvider } from "types/generated";
import useBlockNavigationModal from "hooks/useBlockNavigationModal";
import { InternalFormData } from "components/Forms/SourceCode/types";

import { ModuleSettingsContext } from "../Context";
import { useUpdateModule } from "../hooks/useUpdateModule";
import { getDefaultValues, mapModuleSourceCodeUpdateInput } from "./utils";

export type ModuleSettingsSourceCodeFormFields = {
  namespace: string;
  repository: string;
  projectRoot: string;
  branch: string;
  provider: VcsProvider;
  vcsIntegrationId: string | undefined;
  repositoryURL: string;
  gitSparseCheckoutPaths: Array<{ value: string }>;
};

const ModuleSettingsSourceCodeEditForm = () => {
  const { module } = useTypedContext(ModuleSettingsContext);
  const { updateModule, loading } = useUpdateModule({ module });
  const [enableSparseCheckout, setEnableSparseCheckout] = useState<InternalFormData>({
    useGitCheckout: module.vcsIntegration?.details?.useGitCheckout,
  });

  const builderForm = useForm<ModuleSettingsSourceCodeFormFields>({
    defaultValues: getDefaultValues(module),
    mode: "onChange",
  });

  const {
    handleSubmit,
    reset,
    watch,
    formState: { isDirty, isSubmitSuccessful },
    trigger,
  } = builderForm;

  useBlockNavigationModal({ isNavigationBlocked: isDirty });

  const onSubmit = useCallback(
    () =>
      handleSubmit((values: ModuleSettingsSourceCodeFormFields) => {
        return updateModule(mapModuleSourceCodeUpdateInput(values));
      })(),
    [updateModule, handleSubmit]
  );

  useEffect(() => {
    if (isSubmitSuccessful) {
      reset(getDefaultValues(module));
    }
  }, [isSubmitSuccessful, module, reset]);

  const resetFormFields = (type: VcsProvider) => {
    reset({
      repository: "",
      branch: "",
      projectRoot: "",
      repositoryURL: "",
      provider: type,
      vcsIntegrationId: undefined,
      gitSparseCheckoutPaths: [],
    });

    trigger();
  };

  const values = watch();

  return (
    <FormProvider {...builderForm}>
      <FormSourceCode
        spaceId={module.spaceDetails.id}
        provider={values.provider}
        vcsIntegrationId={values.vcsIntegrationId}
        repository={values.repository}
        resetFormFields={resetFormFields}
        projectType={SourceCodeProjects.Module}
        useGitCheckout={enableSparseCheckout.useGitCheckout}
        setInternalFormData={setEnableSparseCheckout}
      />
      <FormFooterEdit
        onSubmit={onSubmit}
        onCancel={() => reset(getDefaultValues(module))}
        isDirty={isDirty}
        submitLoading={loading}
      />
    </FormProvider>
  );
};

export default ModuleSettingsSourceCodeEditForm;
